export type RouteName =
  | 'home'
  | 'login'
  | 'distributor'
  | 'catalog'
  | 'productDetail'
  | 'cart'
  | 'delivery'
  | 'payment'
  | 'processing'
  | 'confirmed'
  | 'orders'
  | 'orderDetail'
  | 'account'

/**
 * A route as data rather than JSX, so both routers can be built from the same
 * definitions instead of two hand-synced trees.
 */
export interface RouteDef {
  name: RouteName
  /** Path template shared by React Router and Expo Router. */
  path: string
  titleKey: string
}

export const ROUTE_MAP: readonly RouteDef[] = [
  // `home` is the one route that exists *before* a session does: the bare
  // storefront URL a customer can type or be linked to. Logging in there is
  // what creates the session every other route is scoped by.
  { name: 'home', path: '/:slug', titleKey: 'login.title' },
  // `login` and `distributor` come before the session exists in the design's
  // flow, but the storefront is still addressed by slug and session — the
  // session id is minted by the channel that opened the webview, not by us.
  { name: 'login', path: '/:slug/:sessionId/login', titleKey: 'login.title' },
  {
    name: 'distributor',
    path: '/:slug/:sessionId/distributor',
    titleKey: 'distributor.title',
  },
  { name: 'catalog', path: '/:slug/:sessionId', titleKey: 'catalog.title' },
  {
    name: 'productDetail',
    path: '/:slug/:sessionId/product/:sku',
    titleKey: 'pdp.title',
  },
  { name: 'cart', path: '/:slug/:sessionId/cart', titleKey: 'cart.title' },
  { name: 'delivery', path: '/:slug/:sessionId/delivery', titleKey: 'delivery.title' },
  { name: 'payment', path: '/:slug/:sessionId/payment', titleKey: 'payment.title' },
  {
    name: 'processing',
    path: '/:slug/:sessionId/processing',
    titleKey: 'processing.title',
  },
  { name: 'confirmed', path: '/:slug/:sessionId/confirmed', titleKey: 'confirmed.title' },
  { name: 'orders', path: '/:slug/:sessionId/orders', titleKey: 'orders.title' },
  {
    name: 'orderDetail',
    path: '/:slug/:sessionId/orders/:orderId',
    titleKey: 'orderDetail.title',
  },
  { name: 'account', path: '/:slug/:sessionId/account', titleKey: 'account.title' },
] as const

export const routeByName = (name: RouteName): RouteDef => {
  const found = ROUTE_MAP.find((r) => r.name === name)
  if (!found) throw new Error(`Unknown route: ${name}`)
  return found
}

/** The `:params` a path template names, in order of appearance. */
export const paramsOf = (path: string): string[] =>
  [...path.matchAll(/:([A-Za-z][A-Za-z0-9]*)/g)].map((match) => match[1] as string)

/**
 * A concrete URL for a route.
 *
 * Replaces `routeByName(name).path.replace(':slug', slug).replace(':sessionId', …)`,
 * which was written out in four places before the screens landed and would have
 * been written out in a dozen after. Both routers build their links from
 * `ROUTE_MAP`, so both need this.
 *
 * A missing or empty param throws rather than producing `/acme/:sessionId/cart`.
 * That URL routes — React Router happily treats `:sessionId` as the literal
 * value of `sessionId` — so it fails as a *query* several screens later, with
 * the session id `":sessionId"`. Failing at the link is the cheaper failure.
 */
export const screenPath = (
  name: RouteName,
  params: Readonly<Record<string, string | undefined>>,
): string => {
  const { path } = routeByName(name)

  return paramsOf(path).reduce((url, param) => {
    const value = params[param]
    if (value === undefined || value === '') {
      throw new Error(`Cannot build the "${name}" path: missing "${param}"`)
    }
    return url.replace(`:${param}`, encodeURIComponent(value))
  }, path)
}
